<template>
  <sign-in
    class="register"
  >
    <template
      #content
    >
      <div
        v-if="accountDataReady"
        class="_head"
      >
        <div class="_title">
          <div v-if="isLandingPage">
            <h3>
              {{ $t('register.landingPageTitle') }}
            </h3>
            <h1>{{ publicAccountData.companyName }}</h1>
          </div>
          <div v-else>
            <h1>
              {{ $t('register.title') }}
            </h1>
          </div>
        </div>
        <div
          v-if="!isLandingPage"
          class="_sub-title"
        >
          <div class="_perk">
            <m-icon
              class="_icon"
              type="check-mark"
            />
            <div>
              {{ $t('register.perk1') }}
            </div>
          </div>
          <div class="_perk">
            <m-icon
              class="_icon"
              type="check-mark"
            />
            <div>
              {{ $t('register.perk2') }}
            </div>
          </div>
          <div class="_perk">
            <m-icon
              class="_icon"
              type="check-mark"
            />
            <div>
              {{ $t('register.perk3') }}
            </div>
          </div>
        </div>
      </div>
    </template>
    <sign-in-content
      v-if="accountDataReady"
      class="_register"
    >
      <div
        v-if="!samlActive"
        class="_sso"
      >
        <div
          v-if="showGoogleLogin"
          class="_btn"
        >
          <m-btn
            block
            :loading="googleLoading"
            @click="goToGoogle"
          >
            <m-icon
              size="16"
              type="google-color"
              class="_sso-icon"
            />
            {{ $t('register.viaGoogle') }}
          </m-btn>
        </div>
        <div
          v-if="showMSLogin"
          class="_btn"
        >
          <m-btn
            block
            :loading="microsoftLoading"
            @click="goToMicrosoft"
          >
            <m-icon
              size="16"
              type="microsoft-color"
              class="_sso-icon"
            />
            {{ $t('register.viaMicrosoft') }}
          </m-btn>
        </div>
        <div
          v-if="showSaml"
          class="_btn"
        >
          <m-btn
            block
            @click="samlOverride = true"
          >
            <m-icon
              size="16"
              type="key"
              class="_sso-icon"
            />
            {{ $t('register.viaSaml') }}
          </m-btn>
        </div>
      </div>
      <m-divider
        v-if="!samlActive && showDivider"
        small
      />
      <saml-login v-if="samlActive" />
      <div
        v-if="samlActive && hasOtherLoginOptions"
        class="_saml-back"
      >
        <m-link
          inherit-color
          @click="samlOverride = false"
        >
          {{ $t('login.back') }}
        </m-link>
      </div>
      <div v-if="!samlActive && showPasswordScreens">
        <password-login
          v-if="isLandingPage && showPasswordLogin"
          :loading="loading"
          :hide-register-link="true"
          @submit="submitPasswordLogin"
        />
        <div v-else>
          <div
            class="_item"
          >
            <div class="_label">
              {{ $t('general.workMail') }}
            </div>
            <m-text-field
              v-model:value="email"
              auto-focus
              allow-clear
              :placeholder="$t('register.enterEmail')"
              @keydown.enter="submit"
            >
              <template #prefix>
                <m-icon
                  type="mail"
                  style="color: rgba(0, 0, 0, .25);"
                />
              </template>
            </m-text-field>
            <div
              v-if="errorMessage !== ''"
              class="_error-message"
            >
              {{ errorMessage }}
            </div>
          </div>
          <m-transition-expand>
            <div v-if="showConfirm">
              <div class="_hint">
                {{ $t('register.hint1') }} <br>
                {{ $t('register.hint2') }}
              </div>
              <div class="_item">
                <m-text-field
                  v-model:value="code"
                  auto-focus
                  :placeholder="$t('register.codePlaceholder')"
                  @keydown.enter="submitConfirm"
                />
              </div>
            </div>
          </m-transition-expand>
          <div class="_action">
            <m-btn
              :loading="loading"
              color="black"
              :button-style="{ width: '100%' }"
              :style="{ width: '100%' }"
              @click="submit"
            >
              {{ buttonText }}
            </m-btn>
          </div>
        </div>
      </div>
      <i18n-t
        v-if="showTerms"
        keypath="register.terms"
        tag="div"
        class="_terms"
      >
        <template
          #tos
        >
          <m-link
            :href="TERMS_URL"
            class="_link"
            target="_blank"
            inherit-color
          > {{ $t('register.tos') }}
          </m-link>
        </template>
        <template
          #privacyPolicy
        >
          <m-link
            :href="PRIVACY_URL"
            class="_link"
            target="_blank"
            inherit-color
          > {{ $t('register.privacyPolicy') }}
          </m-link>
        </template>
      </i18n-t>
      <div
        v-if="!isLandingPage"
        class="_bottom"
      >
        <div>
          {{ $t('login.haveAccount') }}
          <m-link
            :to="login"
            inherit-color
            underlined
          >
            {{ $t('login.logIn') }}
          </m-link>
        </div>
      </div>
    </sign-in-content>
    <div
      class="_turnstile-overlay"
      :class="{ '-active': (pendingAction !== null && turnstileInteractive && !turnstileToken) || turnstileError }"
    >
      <m-card class="_turnstile-card">
        <div v-if="turnstileError">
          <div class="_turnstile-header">
            {{ $t('register.verificationFailed') }}
          </div>
          <m-btn
            color="black"
            class="_retry-btn"
            block
            @click="retryTurnstile"
          >
            {{ $t('register.tryAgain') }}
          </m-btn>
        </div>
        <div
          v-else
          class="_turnstile-header"
        >
          {{ $t('register.verifyHuman') }}
        </div>
        <vue-turnstile
          ref="turnstile"
          v-model="turnstileToken"
          :class="{ '_turnstile': true, '-visible': turnstileInteractive }"
          appearance="interaction-only"
          :site-key="turnstileSiteKey"
          @before-interactive="turnstileInteractive = true"
          @error="turnstileError = true"
        />
      </m-card>
    </div>
  </sign-in>
</template>

<script>
import PasswordLogin from '@/components/PasswordLogin.vue';
import SamlLogin from '@/components/SamlLogin.vue';
import SignIn from '@/components/SignIn.vue';
import SignInContent from '@/components/SignInContent.vue';
import Url from 'domurl';
import VueTurnstile from 'vue-turnstile';
import useAccountSwitch from '@/composables/account-switch';
import useAppStart from '@/composables/app-start/use-app-start';
import useMe from '@/composables/me';
import usePageVisits from '@/composables/page-visits';
import { PRIVACY_URL, TERMS_URL } from 'shared/transformers';
import { browserLanguage } from '@/lib/language';
import { doConfirmRegisterCode, doLoginUser, doRegisterViaEmail } from '@/api/sentinel';
import { emailRegex } from 'shared/lib/email-regex';
import { environmentVariable, resolveEnvironmentVariable } from '@/lib/env';
import { errorType } from '@/constants/sentinel.json';
import { getQueryParam } from '@/lib/route';
import { isEmpty } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { mapActions, mapState } from 'vuex';
import { routeName } from 'shared/constants.json';

// Cloudflare Turnstile tokens are single-use and expire 300s after issuance.
// We refetch a fresh token shortly before that to avoid submitting an expired one.
const TURNSTILE_TOKEN_MAX_AGE_MS = 270 * 1000;

export default {
  name: 'Register',
  components: { PasswordLogin, SamlLogin, SignInContent, SignIn, VueTurnstile },
  setup() {
    const pageVisitSvc = usePageVisits();
    const { start: startApp } = useAppStart(pageVisitSvc);
    const { switchAccount } = useAccountSwitch();
    const { reachableAccounts, currentTenant } = useMe();
    return { startApp, switchAccount, reachableAccounts, currentTenant };
  },
  data() {
    return {
      loading: false,
      googleLoading: false,
      microsoftLoading: false,
      password: '',
      email: '',
      code: '',
      errorMessage: '',
      PRIVACY_URL,
      TERMS_URL,
      showConfirm: false,
      showPasswordLogin: true,
      turnstileToken: '',
      turnstileTokenIssuedAt: null,
      turnstileInteractive: false,
      turnstileError: false,
      pendingAction: null,
      turnstileSiteKey: resolveEnvironmentVariable(environmentVariable.CLOUDFLARE_TURNSTILE_SITE_KEY),
      samlOverride: null,
    };
  },
  computed: {
    ...mapState({
      registerURLs: (state) => state.registerURLs,
      publicAccountData: (state) => state.publicAccountData,
      isLoggedIn: (state) => state.isLoggedIn,
    }),
    login() {
      return { name: routeName.logIn };
    },
    buttonText() {
      if (this.showConfirm) {
        return this.$t('register.continueWithCode');
      }
      return this.$t('register.continueWithEmail');
    },
    queryEmail() {
      return getQueryParam(this.$route, 'email');
    },
    lang() {
      const lang = getQueryParam(this.$route, 'lang');
      if (lang !== '') {
        return lang;
      }
      return browserLanguage();
    },
    slug() {
      return this.$route.params.slug;
    },
    hasSlug() {
      return typeof this.slug !== 'undefined';
    },
    invitedAccount() {
      if (!this.hasSlug) {
        return undefined;
      }
      return this.reachableAccounts.find(
        (account) => account.activeSlug?.slug === this.slug
          || (account.slugs ?? []).some((s) => s.slug === this.slug),
      );
    },
    isLandingPage() {
      return this.hasSlug && this.publicAccountData !== null;
    },
    accountDataReady() {
      return !this.hasSlug || this.publicAccountData !== null;
    },
    showGoogleLogin() {
      return !this.isLandingPage || !this.publicAccountData.restrictGoogleLogin;
    },
    showMSLogin() {
      return !this.isLandingPage || !this.publicAccountData.restrictMicrosoftLogin;
    },
    showPasswordScreens() {
      return !this.isLandingPage || !this.publicAccountData.restrictPasswordLogin;
    },
    showSaml() {
      return this.isLandingPage
        && this.publicAccountData.usesSaml === true
        && this.publicAccountData.samlConfigured === true;
    },
    hasOtherLoginOptions() {
      return this.showGoogleLogin || this.showMSLogin || this.showPasswordScreens;
    },
    samlActive() {
      if (this.samlOverride !== null) {
        return this.samlOverride;
      }
      return this.showSaml && !this.hasOtherLoginOptions;
    },
    showDivider() {
      return this.showPasswordScreens && (this.showMSLogin || this.showGoogleLogin || this.showSaml);
    },
    showTerms() {
      return !this.isLandingPage;
    },
    hasFreshTurnstileToken() {
      return this.turnstileToken !== ''
        && this.turnstileTokenIssuedAt !== null
        && (Date.now() - this.turnstileTokenIssuedAt) < TURNSTILE_TOKEN_MAX_AGE_MS;
    },
  },
  methods: {
    ...mapActions([
      'oauthRegister',
      'getPublicAccountData',
      'getLoggedInUser',
    ]),
    retryTurnstile() {
      this.turnstileError = false;
      this.resetTurnstile();
    },
    resetTurnstile() {
      this.turnstileToken = '';
      this.turnstileTokenIssuedAt = null;
      this.turnstileInteractive = false;
      this.$refs.turnstile.reset();
    },
    withTurnstile(action) {
      if (this.hasFreshTurnstileToken) {
        action();
        return;
      }
      this.pendingAction = action;
      if (this.turnstileToken !== '') {
        // Token exists but is past its max age: discard it and fetch a fresh one.
        // The watcher runs the pending action once the new token arrives.
        this.resetTurnstile();
      }
    },
    oauthRedirect(provider) {
      this.oauthRegister({
        originURL: window.location.href,
        customParams: { turnstileToken: this.turnstileToken, slug: this.slug },
      }).then((response) => {
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }
        window.location.href = this.registerURLs[provider];
      });
    },
    goToGoogle() {
      this.googleLoading = true;
      this.withTurnstile(() => this.oauthRedirect('google'));
    },
    goToMicrosoft() {
      this.microsoftLoading = true;
      this.withTurnstile(() => this.oauthRedirect('microsoft'));
    },
    submit() {
      if (this.showConfirm) {
        this.submitConfirm();
        return;
      }
      this.submitRegister();
    },
    submitPasswordLogin({ email, password }) {
      this.loading = true;
      doLoginUser({
        email,
        password,
      })
        .then((response) => {
          if (response.status === 400) {
            this.$showSnackbar({ color: 'error', message: this.$t('login.emailPasswordCombinationWrong') });
            return;
          }
          if (response.status === 403) {
            this.$showSnackbar({ color: 'error', message: this.$t('login.passwordLoginRestricted') });
            return;
          }
          if (response.status !== 200) {
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            return;
          }

          this.startApp().then(() => {
            this.loading = false;
            this.$router.push('/');
          }).catch(logCatch(() => {
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          }));
        }).finally(() => {
          this.loading = false;
        });
    },
    submitRegister() {
      const reg = new RegExp(emailRegex());
      if (!reg.test(this.email.trim())) {
        this.errorMessage = this.$t('loginForm.notAValidEmail');
        return;
      }

      this.loading = true;
      this.withTurnstile(() => {
        doRegisterViaEmail({
          email: this.email.trim(),
          lang: this.lang,
          slug: this.slug,
          turnstileToken: this.turnstileToken,
        })
          .then((response) => {
            if (response.status !== 200) {
              if (typeof response.data === 'string' && response.data.includes('no such user')) {
                this.$confirm({
                  title: this.$t('register.emailNotFound'),
                  okText: this.$t('general.okay'),
                  okType: 'warning',
                  hideCancel: true,
                });
                return;
              }
              if (typeof response.data === 'string' && response.data.includes('signup domain blocked')) {
                this.$confirm({
                  title: this.$t('register.domainBlocked', { displayName: response.data.split('signup domain blocked:')[1].trim() }),
                  okText: this.$t('general.okay'),
                  okType: 'warning',
                  hideCancel: true,
                });
                return;
              }
              this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
              return;
            }
            this.showConfirm = true;
            this.$router.push({ query: { ...this.$route.query, email: this.email } });
          }).finally(() => {
            this.loading = false;
            this.resetTurnstile();
          });
      });
    },
    submitConfirm() {
      this.loading = true;
      doConfirmRegisterCode({
        email: this.email.trim(),
        code: this.code.trim(),
        lang: this.lang,
        slug: this.slug,
      })
        .then((response) => {
          this.loading = false;
          if (response.status !== 200) {
            if (typeof response.data === 'string' && response.data.includes('signup domain blocked')) {
              this.$confirm({
                title: this.$t('register.domainBlocked', { displayName: response.data.split('signup domain blocked:')[1].trim() }),
                okText: this.$t('general.okay'),
                okType: 'warning',
                hideCancel: true,
              });
              return;
            }
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            return;
          }
          if (!response.data.isValid) {
            this.$showSnackbar({ color: 'error', message: this.$t('register.codeInvalid') });
            return;
          }
          this.startApp()
            .then(() => {
              this.$router.push({ name: routeName.onboarding, query: { setPassword: true } });
            }).catch(logCatch(() => {
              this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            }));
        });
    },
    checkOAuthError() {
      const url = new Url((new Url()).hash);
      if (typeof url.query.err !== 'undefined' && url.query.err !== null) {
        const error = JSON.parse(atob(url.query.err));
        if (error.type === errorType.emailNotMatching) {
          this.$showSnackbar({ color: 'error', message: `${this.$t('error.emailNotMatching')} ${error.value}` });
        } else {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }
      }
    },
    getAccountData() {
      this.getPublicAccountData({ slug: this.slug }).then((response) => {
        if (response.status !== 200 && typeof response.data === 'string' && response.data.includes('could not find account')) {
          this.$showSnackbar({ color: 'error', message: this.$t('register.accountNotFound') });
          return;
        }
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }
      });
    },
  },
  watch: {
    turnstileToken(val) {
      if (!val) {
        return;
      }
      this.turnstileTokenIssuedAt = Date.now();
      if (this.pendingAction) {
        const action = this.pendingAction;
        this.pendingAction = null;
        action();
      }
    },
    queryEmail() {
      this.email = unescape(this.queryEmail);
    },
    email(val) {
      this.showConfirm = false;
      this.errorMessage = '';

      if (val === '') {
        const query = { ...this.$route.query };
        delete query.email;
        this.$router.push(query);
      }
    },
    slug() {
      if (!this.hasSlug) {
        return;
      }
      this.getAccountData();
    },
  },
  mounted() {
    if (typeof this.$route.query.e !== 'undefined') {
      const e = this.$route.query.e;
      const reg = new RegExp(emailRegex());
      if (reg.test(e)) {
        this.email = e;
      }
    }
    if (!isEmpty(this.$route.query.error)) {
      if (this.$route.query.error === 'no_such_user' && this.hasPathName) {
        this.$confirm({
          title: this.$t('register.emailNotFound'),
          okText: this.$t('general.okay'),
          okType: 'warning',
          hideCancel: true,
        });
        return;
      }
      if (this.$route.query.error === errorType.signupDomainBlocked) {
        this.$confirm({
          title: this.$t('register.domainBlocked', { displayName: this.$route.query.displayName }),
          okText: this.$t('general.okay'),
          okType: 'warning',
          hideCancel: true,
        });
        return;
      }
      this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
    }
  },
  created() {
    if (this.isLoggedIn) {
      // An invite link opened in an already-running session (only the URL fragment
      // changes, so app-start does not re-run) lands here. Switch into the invited
      // account when the slug is one we can reach; otherwise fall back to home.
      if (this.invitedAccount && this.invitedAccount.uid !== this.currentTenant?.accountId) {
        this.switchAccount({ uid: this.invitedAccount.uid });
        return;
      }
      this.$router.replace('/');
      return;
    }

    if (this.$route.query.error === 'no_such_user' && this.hasSlug) {
      const query = { ...this.$route.query };
      delete query.email;
      this.$router.push({ ...this.$route, query });
    }
    this.email = this.queryEmail;

    if (typeof this.$route.query.initialEmail !== 'undefined' && this.$route.query.initialEmail !== '') {
      this.email = unescape(this.$route.query.initialEmail);
      this.showPasswordLogin = false;
    }
    this.checkOAuthError();
    if (this.hasSlug) {
      this.getAccountData();
    }
  },
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .register {
    width: 100%;

    ._head {
      padding: 0 3.2rem;
      margin-bottom: 3rem;

      ._icon {
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          max-width: 7rem;
          height: auto;
          border-radius: $default-border-radius;
        }
      }

      ._title {
        text-align: center;

        h1 {
          margin-top: 1rem;
        }
      }

      ._sub-title {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        ._perk {
          display: flex;
          align-items: center;
          margin-right: 2rem;
          margin-bottom: 1rem;

          ._icon {
            margin-right: .4rem;
          }
        }
      }
    }

    ._sso {
      ._btn {
        margin-bottom: .4rem;
        background-color: white;
        border-radius: $btn-border-radius;
      }

      ._sso-icon {
        margin-right: .8rem;
      }
    }

    ._item {
      margin-bottom: 1rem;

      ._label {
        font-size: $font-size-3;
        color: $font-color-secondary;
      }

      ._error-message {
        margin-top: .4rem;
        color: $error-color;
      }
    }

    ._hint {
      margin-bottom: 1rem;
      color: $font-color-secondary;
      text-align: center;
    }

    ._terms {
      margin-top: 3rem;
      font-size: $font-size-2;
      color: $font-color-secondary;
      text-align: center;

      ._link {
        text-decoration: underline;

        &:hover {
          color: $font-color-primary;
          cursor: pointer;
        }
      }
    }

    ._turnstile-overlay {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100vw;
      height: var(--viewport-height-100);
      background-color: map_get($grey, 'darken-2');
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      transition: opacity .2s, visibility .2s;

      &.-active {
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
      }

      ._turnstile-card {
        display: flex;
        align-items: center;
        flex-direction: column;

        ._loading {
          display: flex;
          align-items: center;

          ._spinner {
            margin-right: 1rem;
          }
        }
      }

      ._turnstile-header {
        font-size: $font-size-5;
        font-weight: $font-weight-medium;
      }

      ._retry-btn {
        margin-top: 1rem;
      }

      ._turnstile {
        height: 0;
        overflow: hidden;

        &.-visible {
          height: auto;
          overflow: visible;
          margin-top: 1rem;
        }
      }
    }

    ._bottom {
      margin-top: 5rem;
      font-size: $font-size-4;
      color: $font-color-secondary;
      text-align: center;
    }

    ._saml-back {
      margin-top: 2rem;
      font-size: $font-size-3;
      color: $font-color-secondary;
      text-align: center;
      text-decoration: underline;
    }
  }
</style>
